import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, ShieldAlert, CalendarHeart, Lock, CheckCheck, Check, ArrowLeft, X, Star, Gift } from "lucide-react";
import { GiftModal, GIFT_ICONS, ThankYouButton } from "../components/GiftModal";
import api from "../lib/api";
import { PhotoImg, StatusTag, GoldButton, GhostButton } from "../components/common";
import { useWs } from "../context/WsContext";
import { useAuth } from "../context/AuthContext";

const inputCls = "w-full rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none px-4 py-2.5 text-sm text-[#FBF8F3] placeholder:text-[#9E978E]";

function DatePlannerModal({ connectionId, plan, onClose, onSaved }) {
  const [f, setF] = useState({ date: plan?.date || "", time: plan?.time || "", location: plan?.location || "", activity: plan?.activity || "", notes: plan?.notes || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/connections/${connectionId}/date-plan`, f); toast.success("Date proposed"); onSaved(); onClose(); }
    catch (e) { toast.error("Failed to propose"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md db-glass rounded-3xl border border-[#D4AF37]/25 p-6 db-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4"><div><div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]">Optional · Plan Together</div><h3 className="font-serif text-2xl text-[#FBF8F3] mt-1">Date Planner</h3></div><button onClick={onClose} className="text-[#9E978E]"><X size={22} /></button></div>
        <p className="text-xs text-[#9E978E] mb-3">Totally optional — jot down whatever you two agree on. Every field can be left blank, and you're free to meet wherever and however you like.</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="dateplan-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} />
            <input data-testid="dateplan-time" type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className={inputCls} />
          </div>
          <input data-testid="dateplan-location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Where you'll meet (optional)" className={inputCls} />
          <input data-testid="dateplan-activity" value={f.activity} onChange={(e) => setF({ ...f, activity: e.target.value })} placeholder="What you'll do (optional)" className={inputCls} />
          <textarea data-testid="dateplan-notes" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Any notes (optional)" className={inputCls + " resize-none"} />
          <div className="flex items-start gap-2 text-xs text-[#9E978E] bg-[#0C0A0A]/50 border border-[#D4AF37]/15 rounded-xl p-3"><ShieldAlert size={14} className="text-[#D4AF37] mt-0.5" /> Tip: public places are safest. DateBid doesn't assign venues, hold the date amount, or guarantee payment — those are between you two.</div>
          <GoldButton data-testid="dateplan-submit-btn" onClick={save} disabled={busy} className="w-full">Save Plan</GoldButton>
        </div>
      </div>
    </div>
  );
}

function ReviewModal({ connectionId, onClose, onSaved }) {
  const [f, setF] = useState({ overall: 5, would_meet_again: true, respectful: true, showed_up: true, feedback: "" });
  const submit = async () => {
    try { await api.post(`/connections/${connectionId}/review`, f); toast.success("Review submitted"); onSaved(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md db-glass rounded-3xl border border-[#D4AF37]/25 p-6 db-fade-up" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-2xl text-[#FBF8F3] mb-4">How was your date?</h3>
        <div className="flex gap-1 mb-4">{[1, 2, 3, 4, 5].map((n) => <button key={n} data-testid={`review-star-${n}`} onClick={() => setF({ ...f, overall: n })}><Star size={28} className={n <= f.overall ? "text-[#D4AF37]" : "text-[#3a2c30]"} fill={n <= f.overall ? "currentColor" : "none"} /></button>)}</div>
        {[["showed_up", "They showed up"], ["respectful", "They were respectful"], ["would_meet_again", "I'd meet them again"]].map(([k, label]) => (
          <label key={k} className="flex items-center justify-between py-2 text-sm text-[#D8D2C9]"><span>{label}</span><input type="checkbox" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} className="accent-[#D4AF37] h-4 w-4" /></label>
        ))}
        <textarea rows={3} value={f.feedback} onChange={(e) => setF({ ...f, feedback: e.target.value })} placeholder="Share your experience (optional)" className={inputCls + " resize-none mt-2"} />
        <div className="flex gap-3 mt-4"><GhostButton onClick={onClose} className="flex-1">Later</GhostButton><GoldButton data-testid="review-submit-btn" onClick={submit} className="flex-1">Submit Review</GoldButton></div>
      </div>
    </div>
  );
}

export default function Messages() {
  const { connectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { send, subscribe } = useWs();
  const [connections, setConnections] = useState([]);
  const [conn, setConn] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(false);
  const [showPlanner, setShowPlanner] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [unlockConfirm, setUnlockConfirm] = useState(false);
  const [showGift, setShowGift] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const scrollRef = useRef(null);
  const typingTO = useRef(null);

  const loadConnections = useCallback(async () => {
    const { data } = await api.get("/connections");
    setConnections(data.connections);
  }, []);

  const loadConn = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/connections/${id}`);
      setConn(data);
      if (data.messaging_unlocked) {
        const m = await api.get(`/conversations/${data.conversation_id}/messages`);
        setMessages(m.data.messages);
        send({ event: "read", conversation_id: data.conversation_id });
      } else setMessages([]);
    } catch (e) { toast.error("Could not open conversation"); }
  }, [send]);

  useEffect(() => { loadConnections(); }, [loadConnections]);
  useEffect(() => { if (connectionId) loadConn(connectionId); else setConn(null); }, [connectionId, loadConn]);

  useEffect(() => {
    const unsub = subscribe((evt) => {
      if (evt.event === "message") {
        if (conn && evt.payload.conversation_id === conn.conversation_id) {
          setMessages((m) => m.find((x) => x.message_id === evt.payload.message_id) ? m : [...m, evt.payload]);
          if (evt.payload.sender_id !== user.user_id) send({ event: "read", conversation_id: conn.conversation_id });
        }
        loadConnections();
      }
      if (evt.event === "typing" && conn && evt.payload.conversation_id === conn.conversation_id && evt.payload.user_id !== user.user_id) {
        setTyping(evt.payload.is_typing);
      }
      if (evt.event === "read" && conn && evt.payload.conversation_id === conn.conversation_id) {
        setMessages((m) => m.map((x) => x.sender_id === user.user_id ? { ...x, read: true } : x));
      }
    });
    return unsub;
  }, [subscribe, conn, user, send, loadConnections]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, typing]);

  const sendMsg = () => {
    if (!text.trim() || !conn) return;
    const ok = send({ event: "message", conversation_id: conn.conversation_id, content: text.trim() });
    if (!ok) { api.post(`/conversations/${conn.conversation_id}/messages`, { content: text.trim() }).then((r) => setMessages((m) => [...m, r.data])).catch(() => toast.error("Send failed")); }
    setText("");
    send({ event: "typing", conversation_id: conn.conversation_id, is_typing: false });
  };

  const onType = (e) => {
    setText(e.target.value);
    if (conn) {
      send({ event: "typing", conversation_id: conn.conversation_id, is_typing: true });
      clearTimeout(typingTO.current);
      typingTO.current = setTimeout(() => send({ event: "typing", conversation_id: conn.conversation_id, is_typing: false }), 1500);
    }
  };

  const openUnlockConfirm = async () => {
    setUnlockConfirm(true);
    try { const { data } = await api.get("/credits/balance"); setCreditBalance(data.credits); } catch (e) {}
  };

  const unlock = async () => {
    try {
      await api.post(`/connections/${conn.connection_id}/unlock`);
      toast.success("Conversation unlocked");
      setUnlockConfirm(false);
      loadConn(conn.connection_id);
    } catch (e) {
      if (e.response?.status === 402) { toast.error(e.response.data.detail); setUnlockConfirm(false); navigate("/wallet"); }
      else toast.error(e.response?.data?.detail || "Could not unlock");
    }
  };

  const confirmDate = async () => { await api.post(`/connections/${conn.connection_id}/date-plan/confirm`); toast.success("Date confirmed"); loadConn(conn.connection_id); };
  const completeDate = async (happened) => {
    const { data } = await api.post(`/connections/${conn.connection_id}/complete`, { happened });
    if (data.outcome === "COMPLETED") { toast.success("Date marked complete — leave a review!"); setShowReview(true); }
    else toast.success("Response recorded");
    loadConn(conn.connection_id);
  };

  // List view (mobile) when no connection selected
  const showList = !connectionId;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <div className="grid lg:grid-cols-12 gap-6">
        <div className={`lg:col-span-4 ${!showList ? "hidden lg:block" : ""}`}>
          <h2 className="font-serif text-2xl text-[#FBF8F3] mb-4">Conversations</h2>
          {connections.length === 0 ? <p className="text-sm text-[#9E978E]">Accepted offers will appear here.</p> : (
            <div className="space-y-2">
              {connections.map((c) => (
                <button key={c.connection_id} data-testid={`connection-${c.connection_id}`} onClick={() => navigate(`/messages/${c.connection_id}`)}
                  className={`w-full flex items-center gap-3 rounded-xl p-3 border transition-colors text-left ${connectionId === c.connection_id ? "border-[#D4AF37]/50 bg-[#541224]/30" : "border-white/8 bg-[#141113]/60 hover:border-[#D4AF37]/30"}`}>
                  <div className="h-12 w-12 rounded-full overflow-hidden border border-white/10 shrink-0"><PhotoImg photoId={c.other?.main_photo_id} fallbackInitial={c.other?.display_name?.[0]} className="w-full h-full object-cover" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between"><span className="font-medium text-[#FBF8F3] truncate">{c.other?.display_name}</span>{c.unread > 0 && <span className="bg-[#751A32] text-white text-[10px] rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center">{c.unread}</span>}</div>
                    <p className="text-xs text-[#9E978E] truncate">{c.last_message?.content || (c.messaging_unlocked ? "Say hello" : "🔒 Locked")}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={`lg:col-span-8 ${showList ? "hidden lg:block" : ""}`}>
          {!conn ? (
            <div className="hidden lg:flex h-[70vh] items-center justify-center text-[#9E978E] rounded-2xl border border-white/8 bg-[#141113]/40"><p className="font-serif text-xl">Select a conversation</p></div>
          ) : (
            <div className="flex flex-col h-[78vh] rounded-2xl border border-white/10 bg-[#141113]/90 overflow-hidden">
              <div className="flex items-center gap-3 p-4 border-b border-white/8 db-glass">
                <button onClick={() => navigate("/messages")} className="lg:hidden text-[#9E978E]"><ArrowLeft size={20} /></button>
                <div className="h-10 w-10 rounded-full overflow-hidden border border-white/10 cursor-pointer" onClick={() => navigate(`/profile/${conn.other.user_id}`)}><PhotoImg photoId={conn.other?.main_photo_id} fallbackInitial={conn.other?.display_name?.[0]} className="w-full h-full object-cover" /></div>
                <div className="flex-1">
                  <div className="font-serif text-lg text-[#FBF8F3]">{conn.other?.display_name}</div>
                  <div className="text-xs text-[#9E978E] flex items-center gap-2">${conn.offer_amount} offer <StatusTag status={conn.offer_status} /></div>
                </div>
                {conn.messaging_unlocked && <button data-testid="send-gift-open-btn" onClick={() => setShowGift(true)} className="rounded-full bg-[#541224]/60 border border-[#D4AF37]/30 px-3 py-2 text-[#D4AF37] hover:bg-[#541224] flex items-center gap-1.5 text-xs"><Gift size={15} /> Gift</button>}
                {conn.messaging_unlocked && <button data-testid="date-planner-open-btn" onClick={() => setShowPlanner(true)} className="rounded-full bg-[#541224]/60 border border-[#D4AF37]/30 px-3 py-2 text-[#D4AF37] hover:bg-[#541224] flex items-center gap-1.5 text-xs"><CalendarHeart size={15} /> Plan Date</button>}
              </div>

              <div className="px-4 py-2 bg-[#541224]/20 border-b border-[#D4AF37]/15 flex items-center gap-2 text-[11px] text-[#C9A96E]"><ShieldAlert size={13} /> Meet in public · Never share home address or financial details.</div>

              {conn.date_plan && conn.date_plan.status !== "CANCELLED" && (
                <div className="px-4 py-3 bg-[#0C0A0A]/60 border-b border-white/8 flex items-center justify-between flex-wrap gap-2">
                  <div className="text-sm text-[#D8D2C9]"><CalendarHeart size={14} className="inline text-[#D4AF37] mr-1.5" />{conn.date_plan.activity || "Date"} · {conn.date_plan.date} {conn.date_plan.time} · {conn.date_plan.location} <StatusTag status={conn.date_plan.status} /></div>
                  <div className="flex gap-2">
                    {conn.date_plan.status === "PROPOSED" && !conn.date_plan.confirmations?.[user.user_id] && <GoldButton data-testid="date-planner-confirm-btn" onClick={confirmDate} className="px-3 py-1.5 text-xs">Confirm Date</GoldButton>}
                    {conn.date_plan.status === "CONFIRMED" && conn.offer_status !== "COMPLETED" && (
                      <>
                        <GoldButton data-testid="date-complete-yes-btn" onClick={() => completeDate(true)} className="px-3 py-1.5 text-xs">Date Happened ✓</GoldButton>
                        <GhostButton onClick={() => completeDate(false)} className="px-3 py-1.5 text-xs">Didn't Happen</GhostButton>
                      </>
                    )}
                    {conn.offer_status === "COMPLETED" && <GoldButton data-testid="leave-review-btn" onClick={() => setShowReview(true)} className="px-3 py-1.5 text-xs">Leave Review</GoldButton>}
                  </div>
                </div>
              )}

              {!conn.messaging_unlocked ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                  <div className="h-16 w-16 rounded-full bg-[#541224]/40 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] mb-4"><Lock size={26} /></div>
                  <h3 className="font-serif text-2xl text-[#FBF8F3]">Unlock this conversation</h3>
                  {conn.can_unlock ? (
                    <>
                      <p className="text-sm text-[#9E978E] mt-2 max-w-xs">Spend {conn.unlock_credits} credits to open a private conversation with {conn.other?.display_name}. Pricing is based on your ${conn.offer_amount} accepted offer.</p>
                      <GoldButton data-testid="unlock-conversation-btn" onClick={openUnlockConfirm} className="mt-5">Unlock for {conn.unlock_credits} credits</GoldButton>
                      <button onClick={() => navigate("/wallet")} className="text-xs text-[#D4AF37] mt-3 underline underline-offset-2">Need more credits?</button>
                    </>
                  ) : (
                    <p data-testid="unlock-waiting-note" className="text-sm text-[#9E978E] mt-2 max-w-xs">Waiting for {conn.other?.display_name} to unlock your conversation. Messaging is free for you once it opens.</p>
                  )}
                </div>
              ) : (
                <>
                  <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.length === 0 && <p className="text-center text-sm text-[#9E978E] mt-8">Say hello and start planning your evening.</p>}
                    {messages.map((m) => {
                      const mine = m.sender_id === user.user_id;
                      return (
                        <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#541224] text-[#FBF8F3] rounded-br-sm" : "bg-[#1C171A] text-[#D8D2C9] rounded-bl-sm border border-white/8"}`}>
                            {m.kind === "gift" && (() => { const GI = GIFT_ICONS[m.gift?.icon] || Gift; return (
                              <div data-testid={`gift-bubble-${m.message_id}`} className="flex items-center gap-2 mb-1 text-[#D4AF37]"><GI size={20} /><span className="font-serif text-base">{m.gift?.label}</span></div>
                            ); })()}
                            <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
                            {m.kind === "gift" && !mine && <ThankYouButton m={m} onSent={(msg) => setMessages((list) => [...list.map((x) => x.message_id === m.message_id ? { ...x, gift: { ...x.gift, thanked: true } } : x), ...(msg && !list.find((y) => y.message_id === msg.message_id) ? [msg] : [])])} />}
                            <div className={`flex items-center gap-1 mt-1 text-[10px] ${mine ? "text-[#C9A96E]" : "text-[#9E978E]"}`}>
                              {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              {mine && (m.read ? <CheckCheck size={12} /> : <Check size={12} />)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {typing && <div className="flex justify-start"><div className="bg-[#1C171A] border border-white/8 rounded-2xl px-4 py-3 db-typing text-[#D4AF37]"><span>•</span><span>•</span><span>•</span></div></div>}
                  </div>
                  <div className="p-3 border-t border-white/8 flex items-center gap-2">
                    <input data-testid="chat-input-field" value={text} onChange={onType} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder="Type a message…" className={inputCls} />
                    <button data-testid="chat-send-btn" onClick={sendMsg} className="db-offer-btn rounded-full h-11 w-11 flex items-center justify-center shrink-0"><Send size={18} /></button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showPlanner && conn && <DatePlannerModal connectionId={conn.connection_id} plan={conn.date_plan} onClose={() => setShowPlanner(false)} onSaved={() => loadConn(conn.connection_id)} />}
      {showGift && conn && <GiftModal connectionId={conn.connection_id} recipientName={conn.other?.display_name} onClose={() => setShowGift(false)} onSent={() => loadConn(conn.connection_id)} />}
      {showReview && conn && <ReviewModal connectionId={conn.connection_id} onClose={() => setShowReview(false)} onSaved={() => loadConn(conn.connection_id)} />}
      {unlockConfirm && conn && (() => {
        const cost = conn.unlock_credits;
        const insufficient = creditBalance != null && creditBalance < cost;
        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setUnlockConfirm(false)} data-testid="unlock-confirm-modal">
            <div className="w-full max-w-sm db-glass rounded-3xl border border-[#D4AF37]/25 p-6 db-fade-up" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-serif text-2xl text-[#FBF8F3] mb-1">Unlock messaging?</h3>
              <p className="text-sm text-[#9E978E] mb-4">Open a private conversation with {conn.other?.display_name}.</p>
              <div className="rounded-xl bg-[#0C0A0A]/50 border border-[#D4AF37]/15 p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#9E978E]">Cost</span><span className="font-serif text-[#D4AF37]" data-testid="unlock-cost">{cost} credits</span></div>
                {creditBalance != null && <div className="flex justify-between"><span className="text-[#9E978E]">Your balance</span><span className="text-[#D8D2C9]" data-testid="unlock-balance">{creditBalance} credits</span></div>}
                {creditBalance != null && !insufficient && <div className="flex justify-between"><span className="text-[#9E978E]">After unlock</span><span className="text-[#D8D2C9]">{creditBalance - cost} credits</span></div>}
              </div>
              {insufficient ? (
                <div className="mt-4">
                  <p className="text-xs text-[#E58CA0] mb-2" data-testid="unlock-insufficient">You need {cost - creditBalance} more credits for this.</p>
                  <GoldButton data-testid="unlock-buy-credits-btn" onClick={() => navigate("/wallet")} className="w-full">Buy / Top Up Credits</GoldButton>
                </div>
              ) : (
                <div className="flex gap-3 mt-5">
                  <button onClick={() => setUnlockConfirm(false)} className="flex-1 rounded-full py-2.5 text-sm border border-white/15 text-[#9E978E]">Cancel</button>
                  <GoldButton data-testid="unlock-confirm-btn" onClick={unlock} className="flex-1">Use {cost} Credits</GoldButton>
                </div>
              )}
              <button onClick={() => navigate("/credits/how")} className="w-full text-xs text-[#C9A96E] hover:text-[#D4AF37] mt-3">How DateBid credits work</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
